import React, { Fragment, FunctionComponent, useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
  AppAlertDialog,
  StepperContent,
  StepperHeader,
  StepperSubmit
} from '../../../../../shared/general';
import {
  utilsAdditionalPaymentGetDefaultValuesStep2,
  utilsAdditionalPaymentPrepareFormData
} from '../../utils';
import { FormElementDynamicInputTyped } from '../../../../../shared/form-elements';
import {
  CalculatorAdditionalPaymentFormFields,
  CalculatorAdditionalPaymentApiResponse,
  CalculatorAdditionalPaymentStep2FormFields,
  CalculatorAdditionalPaymentApiRequest
} from '../../types';
import { utilNumberToCurrency, utilStringToNumber } from '../../../../../../utils';
import { useFormContext } from '../../../../../../hooks';
import { useAdditionalPaymentStep2FormFields } from '../../config';
import { API } from '../../../../../../services';
import { FormattedMessage, useIntl } from 'react-intl';
import LoaderDots from 'src/components/shared/general/loader-dots/loader-dots';

const LoadingDialog: FunctionComponent<{ isLoading: boolean }> = ({ isLoading }) => (
  <AppAlertDialog open={isLoading} mode={`message`}>
    <div className={`text-center pt-4`}>
      <h3>
        <FormattedMessage
          id={'step2.busyMessage'}
          defaultMessage={`We zijn nu de maandlasten aan het berekenen`}
        />
      </h3>
      <LoaderDots width={100} height={75} />
    </div>
  </AppAlertDialog>
);

export const CalculatorAdditionalPaymentStepperStep2: FunctionComponent<{
  id: number;
  title?: string;
}> = ({ id, title }) => {
  const intl = useIntl();
  const AdditionalPaymentStep2FormFields = useAdditionalPaymentStep2FormFields(intl);

  const [completedText, setCompletedText] = useState('');
  const { form, updateForm, updateResult, setError, isLoading, setLoading } = useFormContext<
    CalculatorAdditionalPaymentApiResponse,
    CalculatorAdditionalPaymentFormFields
  >();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm({
    mode: 'onBlur',
    defaultValues: utilsAdditionalPaymentGetDefaultValuesStep2(intl)
  });

  const showAdditionFormFields = useMemo(
    () => ['annafl', 'linafl'].includes(form.mortgageType),
    [form.mortgageType]
  );

  const onSubmit = useCallback(
    async (formData: Record<keyof CalculatorAdditionalPaymentStep2FormFields, string>) => {
      setLoading(true);
      try {
        const result = await API.post<
          CalculatorAdditionalPaymentApiResponse,
          CalculatorAdditionalPaymentApiRequest
        >(
          'calculator/calculateadditionalpayment',
          utilsAdditionalPaymentPrepareFormData({
            ...form,
            ...formData
          } as CalculatorAdditionalPaymentFormFields)
        );
        updateResult(result);
        updateForm(formData);
        const { additionalPayment } = formData;
        setCompletedText(
          utilNumberToCurrency(utilStringToNumber(additionalPayment), true, 0, intl.locale)
        );
      } catch (e) {
        setError(true);
        console.error('Error calculating additional payment:', e);
      } finally {
        setLoading(false);
      }
    },
    [form, updateResult, updateForm, setCompletedText, setError, setLoading]
  );

  return (
    <Fragment>
      <LoadingDialog isLoading={isLoading} />
      <StepperHeader
        onClose={handleSubmit(onSubmit)}
        title={title}
        id={id}
        completedText={completedText}
      />
      <StepperContent>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep2FormFields>
            formFields={AdditionalPaymentStep2FormFields}
            id={'additionalPayment'}
            register={register}
            errors={errors}
            values={watch()}
            options={{ required: true }}
          />

          {showAdditionFormFields && (
            <FormElementDynamicInputTyped<CalculatorAdditionalPaymentStep2FormFields>
              formFields={AdditionalPaymentStep2FormFields}
              id={'paymentType'}
              register={register}
              errors={errors}
              values={watch()}
              options={{ required: true }}
            />
          )}

          <StepperSubmit
            id={id}
            isValidated={isValid}
            label={intl.formatMessage({
              id: `step2.button.calculate`,
              defaultMessage: `Bereken`
            })}
          />
        </form>
      </StepperContent>
    </Fragment>
  );
};
